import { ReactNode } from "react";

import { DataId, DataSource, Filterable, Indexable, Paged } from "../../general";

import { AccessibilityProps } from "../../AccessibilityProps";
import { withSubComponents } from "../../utilities/withSubComponents";
import { OptionLabelProvider } from "../DataDropdown";
import { InputBaseProps } from "../InputBaseProps";
import { Placeholder } from "../MultiSelectDropdown/Placeholder";
import { Field } from "./Field";
import { Input } from "./Input";

/**
 * The type of data source accepted by the `MultiSelectDataDropdown` component.
 */
export interface MultiSelectDataDropdownDataSource<TRow extends object> extends DataSource<TRow>, Paged, Filterable, Indexable<TRow> {}

/**
 * The `MultiSelectDataDropdown` component properties.
 */
export interface MultiSelectDataDropdownProps<TRow extends object>
  extends InputBaseProps<ReadonlyArray<DataId> | null>,
    Pick<AccessibilityProps, "aria-label" > {
  /**
   * The data source to use to retrieve dropdown items.
   */
  readonly dataSource: MultiSelectDataDropdownDataSource<TRow>;

  /**
   * The function to call returning the label for a particular row.
   */
  readonly optionLabelProvider: OptionLabelProvider<TRow>;

  /**
   * The field to search against. If remote, corresponds to a field alias. If not, corresponds to a dot path to the field on the row.
   */
  readonly searchField?: string;

  /**
   * Content to show after the text.
   */
  readonly prefix?: ReactNode;

  /**
   * Content to show prior to the text.
   */
  readonly suffix?: ReactNode;

  /**
   * The placeholder text.
   */
  readonly placeholder?: string;
}

/**
 * The `MultiSelectDataDropdown` component sub components.
 */
export interface MultiSelectDataDropdownSubComponents {
  /**
   * A React Hook Form compatible `MultiSelectDataDropdown` component. For more information, see the documentation for
   * the <a href="https://react-hook-form.com/api">React Hook Form API</a>.
   */
  readonly Field: typeof Field;

  /**
   * A placeholder component for the `MultiSelectDataDropdown` which can be displayed while loading.
   */
  readonly Placeholder: typeof Placeholder;
}

/**
 * A data-driven component which allows for the selection of multiple options from a dropdown list.
 */
export const MultiSelectDataDropdown = withSubComponents(
  {
    Field,
    Placeholder,
  } as MultiSelectDataDropdownSubComponents,
  <TRow extends object>(props: MultiSelectDataDropdownProps<TRow>) => {
    return <Input {...props} optionLabelProvider={props.optionLabelProvider as OptionLabelProvider<object>} />;
  },
);
